import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Translate, X, Plus } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Click-to-translate popover. Wrap any word element to make it translatable.
 * Usage: <TranslateWord>palabra</TranslateWord>
 */
export function TranslateWord({ children, className = '' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const word = typeof children === 'string' ? children : String(children || '').trim();

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!word) return;
    setOpen(true);
    if (result) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/translate/word`, { word, direction: 'auto' }, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      toast.error('Traducción no disponible');
      setOpen(false);
    } finally { setLoading(false); }
  };

  const saveCard = async () => {
    if (!result || saved) return;
    try {
      await axios.post(`${API}/flashcards`, {
        word,
        translation: result.translation,
        example_sentence: result.example_sentence || '',
        direction: result.direction || 'es->de',
      }, { withCredentials: true });
      setSaved(true);
      toast.success('Guardado en tus tarjetas');
    } catch { toast.error('No se pudo guardar'); }
  };

  return (
    <span className="relative inline-block">
      <span
        className={`cursor-pointer underline decoration-dotted decoration-[#AB1C24]/40 hover:decoration-[#AB1C24] ${className}`}
        onClick={handleClick}
        data-testid={`tr-word-${word}`}
      >{children}</span>
      {open && (
        <span className="absolute z-40 left-1/2 -translate-x-1/2 mt-1 w-[260px] bg-white border border-stone-200 rounded-lg shadow-xl p-3 text-left">
          <span className="flex items-start justify-between gap-2">
            <span>
              <span className="block text-xs uppercase tracking-wide text-[#AB1C24] font-bold">{result?.direction || 'Traducción'}</span>
              <span className="block font-semibold text-[#292524] mt-0.5 text-sm">{word}</span>
            </span>
            <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="text-[#78716C] hover:text-[#292524]" data-testid="tr-close">
              <X size={14} />
            </button>
          </span>
          <span className="block mt-2 border-t border-stone-100 pt-2">
            {loading && <span className="text-xs text-[#78716C]">Traduciendo…</span>}
            {result && (
              <>
                <span className="block text-sm text-[#292524] font-medium">→ {result.translation}</span>
                {result.example_sentence && (
                  <span className="block text-xs text-[#78716C] italic mt-1">"{result.example_sentence}"</span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); saveCard(); }}
                  disabled={saved}
                  className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${saved ? 'bg-emerald-100 text-emerald-700' : 'bg-[#AB1C24] text-white hover:bg-[#8A151C]'}`}
                  data-testid="tr-save"
                >
                  {saved ? '✓ Guardada' : <><Plus size={12}/> Guardar tarjeta</>}
                </button>
              </>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * Free-text "look up any word" pad — used inside the Vocabulario subsection.
 */
export function TranslatorBox({ onSaved }) {
  const [word, setWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const translate = async () => {
    if (!word.trim()) return;
    setLoading(true); setResult(null);
    try {
      const res = await axios.post(`${API}/translate/word`, { word: word.trim(), direction: 'auto' }, { withCredentials: true });
      setResult(res.data);
    } catch { toast.error('Traducción no disponible'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!result) return;
    try {
      await axios.post(`${API}/flashcards`, {
        word: word.trim(),
        translation: result.translation,
        example_sentence: result.example_sentence || '',
        direction: result.direction || 'es->de',
      }, { withCredentials: true });
      toast.success('Guardado en tus tarjetas');
      onSaved?.();
      setWord(''); setResult(null);
    } catch { toast.error('No se pudo guardar'); }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3">
      <p className="font-semibold text-[#292524] flex items-center gap-2">
        <Translate size={18} weight="duotone" className="text-[#AB1C24]" /> Palabra → Wort
      </p>
      <div className="flex gap-2 flex-wrap">
        <input
          className="flex-1 min-w-[180px] border border-stone-300 rounded-md px-3 py-2 text-sm"
          placeholder="Escribe una palabra en español o alemán…"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && translate()}
          data-testid="translator-input"
        />
        <button
          onClick={translate}
          disabled={loading || !word.trim()}
          className="px-4 py-2 bg-[#AB1C24] text-white rounded-md text-sm font-semibold hover:bg-[#8A151C] disabled:opacity-60"
          data-testid="translator-go"
        >{loading ? '...' : 'Traducir'}</button>
      </div>
      {result && (
        <div className="bg-[#FAF9F6] rounded-md p-3 text-sm">
          <p className="text-[#AB1C24] uppercase tracking-wider text-[10px] font-bold">{result.direction}</p>
          <p className="text-[#292524] mt-0.5"><strong>{word}</strong> → <strong>{result.translation}</strong></p>
          {result.example_sentence && <p className="text-[#78716C] italic text-xs mt-1">"{result.example_sentence}"</p>}
          <button
            onClick={save}
            className="mt-2 inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700"
            data-testid="translator-save"
          ><Plus size={12}/> Guardar como tarjeta</button>
        </div>
      )}
    </div>
  );
}
